import {FormControl, FormField, Separator} from 'platform/components';
import {Box, Heading, HStack, Show, Space, VStack} from 'platform/foundation';

import {UseFormReturn} from 'react-hook-form';
import {useLocation} from 'react-router-dom';

import {useAccM2StrediskoGetCentresQuery} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {SettingsFooter, SettingsSection} from '@dms/settings/shared';
import {useGetVatRatesOptions} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {FormType} from '../../types/ServiceOrderVariant';
import {getServiceOrderVariantsRoutes} from '../../utils/getServiceOrderVariantsRoutes';

interface AccountingTabProps extends RequiredTestIdProps {
  control: FormControl<FormType>;
  formApi: UseFormReturn<FormType>;
}

export function AccountingTab(props: AccountingTabProps) {
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const serviceOrderVariantsRoutes = getServiceOrderVariantsRoutes(pathname);

  const [vatOptions] = useGetVatRatesOptions({fullLabel: true});

  const {data: serviceCentres, isLoading} = useAccM2StrediskoGetCentresQuery();

  const serviceCentresOptions = serviceCentres?.m2centres?.map((centre) => ({
    label: centre?.name ?? '',
    value: centre?.code ?? '',
  }));

  const excludedServiceCenterIds = props.formApi.watch('accounting.excludedServiceCenterIds');

  const filteredServiceCentresOptions =
    serviceCentresOptions?.filter((option) => !excludedServiceCenterIds?.includes(option.value)) ??
    [];

  return (
    <>
      <SettingsSection>
        <VStack spacing={4}>
          <FormField
            control={props.control}
            type="radio"
            label={i18n.t('entity.accounting.labels.parametersForTax')}
            options={[
              {label: i18n.t('entity.accounting.lables.vatDeductible'), value: 'vatDeductible'},
              {
                label: i18n.t('entity.accounting.lables.notVatDeductible'),
                value: 'notVatDeductible',
              },
            ]}
            name="accounting.taxDeductible"
            data-testid={suffixTestId('accounting', props)}
          />
          <Show when={props.formApi.watch('accounting.taxDeductible') === 'notVatDeductible'}>
            <FormField
              control={props.control}
              type="choice"
              label={i18n.t('entity.accounting.labels.fixedVat')}
              options={vatOptions}
              name="accounting.fixedVatType"
              menuInPortal
              data-testid={suffixTestId('fixedVatType', props)}
            />
          </Show>
          <Separator spacing={0} />
          <Heading size={4}>{i18n.t('entity.accounting.labels.serviceCentres')}</Heading>
          <FormField
            control={props.control}
            label={i18n.t('entity.accounting.labels.excludedServiceCentres')}
            name="accounting.excludedServiceCenterIds"
            options={serviceCentresOptions ?? []}
            isLoading={isLoading}
            type="multiChoice"
            data-testid={suffixTestId('excludedServiceCentres', props)}
          />
          <HStack spacing={4} align="center">
            <Box flex={1}>
              <FormField
                control={props.control}
                label={i18n.t('entity.accounting.labels.defaultServiceCenter')}
                name="accounting.defaultServiceCenterId"
                options={filteredServiceCentresOptions}
                isLoading={isLoading}
                type="choice"
                data-testid={suffixTestId('defaultServiceCenter', props)}
              />
            </Box>
            <Box flex={1}>
              <VStack>
                <Space vertical={5} />
                <FormField
                  control={props.control}
                  type="checkbox"
                  label={i18n.t('entity.accounting.labels.lockDefault')}
                  name="accounting.isDefaultServiceCenterLocked"
                  data-testid={suffixTestId('lockDefaultServiceCenter', props)}
                />
              </VStack>
            </Box>
          </HStack>
        </VStack>
      </SettingsSection>
      <SettingsFooter
        actions={[
          {
            type: 'button',
            variant: 'secondary',
            title: i18n.t('general.actions.discardChanges'),
            onClick: () => navigate(serviceOrderVariantsRoutes.overview),
          },
          {
            type: 'form-button',
            control: props.control,
            buttonType: 'submit',
            variant: 'primary',
            title: i18n.t('general.actions.saveChanges'),
          },
        ]}
        data-testid={suffixTestId('footer', props)}
      />
    </>
  );
}
